import { EnclosureType } from '@/types/enclosure/EnclosureType';
import { FullName } from '@/types/FullName';
import { UserDTO } from '@/types/user/UserDTO';
import { formatRussianPhone } from '@/utils/phone-utils';
import { fullNameRegExp } from '@/utils/validation-utils';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import type { PhoneNumber } from 'libphonenumber-js/max';

export const formatCurrency = (
  value: number | string,
  maximumFractionDigits?: number,
  minimumFractionDigits?: number,
): string => {
  if (isNaN(Number(value))) {
    return 'NaN';
  }
  const format = new Intl.NumberFormat('ru-Ru', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: maximumFractionDigits,
    minimumFractionDigits: minimumFractionDigits ? minimumFractionDigits : maximumFractionDigits,
  });
  return format.format(Number(value));
};

const isWhitespace = (char: string): boolean => {
  return /\s/.test(char);
};

export const isBlank = (value: string | undefined | null): boolean => {
  let length;
  if (value == undefined || (length = value.length) === 0) {
    return true;
  }
  for (let i = 0; i < length; i++) {
    if (!isWhitespace(value.charAt(i))) {
      return false;
    }
  }
  return true;
};

export const isNotBlank = (value: string | undefined | null): boolean => {
  return !isBlank(value);
};

/**
 * Selects the Russian plural form that agrees with a count.
 * Agreement follows the last digit, except across 11-19, which always takes the "many" form.
 * So 21 takes the same form as 1, while 11 does not.
 * @param iNumber Count the word has to agree with.
 * @param aEndings Three forms — for 1, for 2-4, and for 5 or more, e.g. `['страница', 'страницы', 'страниц']`.
 *        Give them in the case the sentence needs:
 *        «Повторите попытку через 1 минуту» takes accusative, «1 страница» takes nominative.
 * @returns The form matching the count.
 */
export const getNumEnding = (iNumber: number, aEndings: string[]): string => {
  let sEnding, i;
  iNumber = iNumber % 100;
  if (iNumber >= 11 && iNumber <= 19) {
    sEnding = aEndings[2];
  } else {
    i = iNumber % 10;
    switch (i) {
      case 1:
        sEnding = aEndings[0];
        break;
      case 2:
      case 3:
      case 4:
        sEnding = aEndings[1];
        break;
      default:
        sEnding = aEndings[2];
    }
  }
  return sEnding;
};

export const formatPrisonerShortName = (lastName: string, firstName: string, middleName?: string): string => {
  return [
    upperFirstCaseInSentence(firstName).substring(0, 1) + '.',
    middleName ? upperFirstCaseInSentence(middleName).substring(0, 1) + '.' : '',
    upperFirstCaseInSentence(lastName),
  ]
    .filter((s: string) => isNotBlank(s))
    .join(' ');
};

export const formatPrisonerFullName = (lastName?: string, firstName?: string, middleName?: string): string => {
  return [
    lastName ? upperFirstCaseInSentence(lastName) : '',
    firstName ? upperFirstCaseInSentence(firstName) : '',
    middleName ? upperFirstCaseInSentence(middleName) : '',
  ]
    .filter((s: string) => isNotBlank(s))
    .join(' ');
};

export const formatPrisonerFullNameShort = (lastName: string, firstName: string, middleName?: string): string => {
  return [
    upperFirstCaseInSentence(lastName),
    upperFirstCaseInSentence(firstName).substring(0, 1) + '.',
    middleName ? upperFirstCaseInSentence(middleName).substring(0, 1) + '.' : '',
  ]
    .filter((s: string) => isNotBlank(s))
    .join(' ');
};

export const formatFullName = (lastName: string, firstName: string, middleName?: string): string => {
  return [
    upperFirstCaseInSentence(lastName),
    upperFirstCaseInSentence(firstName),
    upperFirstCaseInSentence(middleName || ''),
  ]
    .filter((s: string) => isNotBlank(s))
    .join(' ');
};

export const formatUserFullName = (user: UserDTO | undefined | null): string => {
  if (user) {
    return formatFullName(user.lastName, user.firstName, user.middleName);
  }
  return '';
};

export const formatUserFullNameShort = (user: UserDTO | undefined | null): string => {
  if (user) {
    return formatFullName(user.lastName, user.firstName);
  }
  return '';
};

/**
 * Renders a stored phone for display: a Russian number in the project mask,
 * a foreign one in international notation, so its country code stays visible and the number remains dialable.
 *
 * The '+7' country code is shared with Kazakhstan, Abkhazia, and South Ossetia,
 * so a value carrying an explicit country code is classified by libphonenumber rather than by its digits.
 * Only the letter and answer author forms and the card issuing form can hold a foreign number;
 * the number bound to an account is Russian by its own rule.
 *
 * The Russian branch keeps `formatRussianPhone` rather than libphonenumber's own output:
 * the library renders `'8 (900) 123-45-67'` nationally and `'+7 900 123 45 67'` internationally,
 * and neither is the `'+7 (900) 123-45-67'` this application has always shown.
 */
export const formatPhoneNumber = (phone?: string): string => {
  if (!phone) {
    return '';
  }
  if (phone.trim().startsWith('+')) {
    const parsed: PhoneNumber | undefined = parsePhoneNumberFromString(phone);
    if (parsed?.country === 'RU') {
      return formatRussianPhone(phone);
    }
    // A value no numbering plan allocates is shown exactly as it was stored:
    // parsing succeeds on '+70001234567' and would otherwise dress it up as a well-formed foreign number.
    return parsed?.isValid() ? parsed.formatInternational() : phone;
  }
  // Without a country code the value can only be Russian national notation.
  const digits: string = phone.replace(/\D/g, '');
  const isRussian: boolean =
    (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) || digits.length === 10;
  return isRussian ? formatRussianPhone(phone) : phone;
};

export const parseFullName = (fullName: string | undefined | null): FullName | null => {
  if (isNotBlank(fullName)) {
    const match: RegExpExecArray | null = fullNameRegExp.exec(fullName || '');
    if (match?.groups) {
      return {
        lastName: upperFirstCaseInSentence(match?.groups?.lastName),
        firstName: upperFirstCaseInSentence(match?.groups?.firstName),
        middleName: upperFirstCaseInSentence(match?.groups?.middleName),
      };
    }
  }
  return null;
};

export const upperFirstCase = (value: string): string => {
  if (isNotBlank(value)) {
    return removeExtraSpaces(value.charAt(0).toUpperCase() + value.slice(1));
  }
  return removeExtraSpaces(value);
};

export const upperFirstCaseInSentence = (value: string): string => {
  if (isNotBlank(value)) {
    const words: string[] = removeExtraSpaces(value).split(' ');
    return words.map((word: string) => upperFirstCase(word)).join(' ');
  }
  return removeExtraSpaces(value);
};

export const removeExtraSpaces = (value: string): string => {
  if (isNotBlank(value)) {
    return value
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .trim();
  }
  return '';
};

export const onlyNumberFormat = (value: string): string => {
  return value.replace(/[^0-9]/g, '');
};

export const formatNumberOfLetters = (value?: number): string | undefined => {
  if (value) {
    return value.toString().replace(/(\d{1,3}(?=(?:\d\d\d)+(?!\d)))/g, '$1' + ' ');
  } else {
    return undefined;
  }
};

/**
 * Formats a letter number for display (with spaces).
 *
 * @param value The letter number to format.
 * @returns A formatted letter number with spaces or empty string.
 */
export const formatLetterNumberDisplay = (value?: number | string): string => {
  if (value === undefined || value === null) {
    return '';
  }
  const numericString: string = value.toString();
  if (numericString === '') {
    return '';
  }
  return numericString.replace(/(\d{1,3}(?=(?:\d\d\d)+(?!\d)))/g, '$1' + ' ');
};

/**
 * Cleans a letter number input string by removing all non-digit characters.
 *
 * @param value The input string of letter number.
 * @returns A string containing only digits.
 */
export const cleanLetterNumberInput = (value?: string | number): string => {
  if (value === undefined || value === null) {
    return '';
  }
  return value.toString().replace(/[^0-9]/g, '');
};

/**
 * Get the name of the enclosure based on its type.
 *
 * @param type The type of the enclosure.
 * @returns The name of the enclosure.
 */
export const getEnclosureName = (type: EnclosureType): string => {
  switch (type) {
    case EnclosureType.CROSSWORD:
      return 'Кроссворд';
    case EnclosureType.SUDOKU:
      return 'Судоку';
    case EnclosureType.NEWS:
      return 'Главные новости';
    case EnclosureType.SPORTS_NEWS:
      return 'Новости спорта';
    default:
      return '';
  }
};

/**
 * Names a photo the way the full-screen view heads it: by its own number where the letter has one,
 * by its position among several otherwise, and as a bare «Фото» for the lone photo of a letter that
 * predates photo numbering.
 *
 * @param photoNumber The number of the photo, as the server formatted it.
 * @param photoIndex  The position of the photo within its letter.
 * @param total       How many photos the letter carries.
 * @returns The label.
 */
export const formatPhotoLabel = (
  photoNumber: string | undefined,
  photoIndex: number | undefined,
  total: number,
): string => {
  if (photoNumber) {
    return `Фото № ${photoNumber}`;
  }
  return total > 1 ? `Фото ${photoIndex || 1}` : 'Фото';
};

/**
 * Reports the photos a reopened draft could not bring back, counted and in agreement.
 * The count is what the sender needs: they are charged per photo,
 * and a draft that quietly returns with fewer of them than it was saved with is the failure this message exists for.
 *
 * @param lost How many photos of the draft could not be restored.
 * @returns The message shown to the sender.
 */
export const formatLostDraftPhotosMessage = (lost: number): string => {
  const photos: string = getNumEnding(lost, ['фотографию', 'фотографии', 'фотографий']);
  return `Не удалось восстановить ${lost} ${photos} черновика. Приложите заново.`;
};

/**
 * Reports the photos a reopened draft could not bring back
 * because the sender is holding as many staged photos as they may at once.
 * It differs from {@link formatLostDraftPhotosMessage} in the only way that matters to the sender:
 * attaching them again cannot work until they free some room,
 * so the server's own wording carries the way out instead of an instruction that would fail.
 *
 * @param lost How many photos of the draft could not be restored.
 * @param reason What the server answered, which names the limit and what to do about it.
 * @returns The message shown to the sender.
 */
export const formatDraftPhotoLimitMessage = (lost: number, reason: string): string => {
  const photos: string = getNumEnding(lost, ['фотографию', 'фотографии', 'фотографий']);
  return `Не удалось восстановить ${lost} ${photos} черновика. ${reason}`;
};
